import _ from 'lodash';

function intelligentlyTruncate(name: string, maxLength: number, separator = '...') {
  if (_.size(name) <= maxLength) {
    return name;
  }
  const correctMaxLength = maxLength - _.size(separator);
  const beforeCharCount = (correctMaxLength / 3) * 2;
  const endCharCount = correctMaxLength / 3;
  const totalCharCount = _.size(name);
  return `${name.substring(0, beforeCharCount)}${separator}${name.substring(
    totalCharCount - endCharCount,
    totalCharCount,
  )}`;
}

/**
 * Returns an object with { key: value } properties from a string split by ':' using the "key:value" format
 * Example:
 * ['key1:val1', 'key2:val2', 'invalid']
 * will result in
 * { key1: 'val1', key2: 'val2' }
 *
 * @param {string[]} paramValues
 * @returns {Record<string, string>}
 */
function splitParamToObjectValue(paramValues: string[]): Record<string, string> {
  return _.chain(paramValues)
    .filter((item) => item.indexOf(':') > -1)
    .map((item) => ({
      key: String(item).substring(0, String(item).indexOf(':')),
      value: String(item).substring(String(item).indexOf(':') + 1),
    }))
    .keyBy('key')
    .mapValues('value')
    .value() as Record<string, string>;
}

/**
 * Copies text to clipboard using modern Clipboard API with fallback to execCommand.
 * Works reliably in modals without DOM manipulation. Never rejects - logs the error and
 * resolves false, so callers can tell a failed copy from a successful one.
 *
 * @param text - The text to copy to the clipboard
 * @param root - Optional root element for textarea attachment (fallback only)
 * @returns Whether the text reached the clipboard
 */
async function copyTextToClipboard(text: string, root?: HTMLElement): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, falling back to execCommand:', err);
    }
  }

  // Fallback: execCommand with temporary textarea (deprecated but needed for Playwright environment)
  try {
    const parent = root || document.body;
    const element = document.createElement('textarea');
    element.value = text;
    element.setAttribute('readonly', '');
    element.style.position = 'absolute';
    element.style.left = '-9999px';
    element.style.zIndex = '9999';
    parent.appendChild(element);

    // Store current selection for restoration
    const selection = document.getSelection();
    const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;

    // Select and copy
    element.select();
    element.setSelectionRange(0, text.length);

    let success = false;
    if (typeof document.execCommand === 'function') {
      success = document.execCommand('copy');
    }

    // Clean up
    parent.removeChild(element);

    // Restore previous selection
    if (selectedRange && selection) {
      selection.removeAllRanges();
      selection.addRange(selectedRange);
    }

    if (!success) {
      console.error('Clipboard copy failed: neither Clipboard API nor execCommand succeeded');
    }

    return success;
  } catch (err) {
    console.error('Fallback clipboard copy failed:', err);
    return false;
  }
}

interface PropertyAndCallback {
  property: string | string[];
  callback: () => void;
}

/**
 * Calls the callback associated with a given property if said property has changed between oldObj and newObj
 *
 * @param oldObj - The old object maybe containing a property
 * @param newObj - The new object maybe containing a property
 * @param callbacksList - The list of properties and associated callbacks
 */
export function notifyIfChanged<T extends object>(oldObj: T, newObj: T, callbacksList: PropertyAndCallback[]): void {
  _.forEach(callbacksList, (callbackItem) => {
    if (_.isArray(callbackItem.property)) {
      if (_.some(callbackItem.property, (property) => _.get(oldObj, property) !== _.get(newObj, property))) {
        callbackItem.callback();
      }
    } else if (_.get(oldObj, callbackItem.property) !== _.get(newObj, callbackItem.property)) {
      callbackItem.callback();
    }
  });
}

/**
 * Determines if the app is being driven by Playwright or Protractor
 */
export function isSystemTest(): boolean {
  return window.navigator.userAgent === 'Playwright' || isSmartlingSystemTest() || isProtractor();
}

export function isSmartlingSystemTest(): boolean {
  return window.navigator.userAgent === 'Playwright Smartling';
}

/**
 * Determines if the app is rendered inside an iframe. A wrapper because window.top is unforgeable and
 * cannot be mocked in tests.
 */
export function isInIframe(): boolean {
  return window.self !== window.top;
}

// @deprecated Use isSystemTest instead
export function isProtractor(): boolean {
  return window.navigator.userAgent.includes('Protractor');
}

/**
 * A helper to throw errors in null chains. Without this, we have to define an anonymous function per call, such as
 * stringValue.match(/[^\/]+$/)?.[0] ?? (() => { throw new Error('Sample error message') })();
 * which gets shortened to stringValue.match(/[^\/]+$/)?.[0] ?? throwError('Sample error message');
 */
export function throwError(error: string): never {
  throw new Error(error);
}

export default {
  intelligentlyTruncate,
  splitParamToObjectValue,
  copyTextToClipboard,
};

export function getMaxSeriesPixels(): number {
  if (isProtractor()) {
    return 100;
  } else if (isSystemTest()) {
    return 500; // To allow for visualizing spikes in downsampnled data
  } else {
    return 5000;
  }
}

export function openLinkBasedOnUserPreferences(url: string, preferNewTab: boolean): void {
  preferNewTab ? window.open(url, '_blank') : (window.location.href = url);
}

export function stripHTML(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
